

import { useMemo, useState } from "react";
import { Link } from 'react-router-dom';
import { ChevronDown } from "lucide-react";
import { useRosterStore } from "@/store/roster-store";
import { useAccountStore } from "@/store/account-store";
import { useGamesQuery } from "@/hooks/useGamesQuery";
import { GameEarningsSheet } from "@/components/dashboard/GameEarningsSheet";
import { RosterCard } from "@/components/dashboard/RosterCard";
import { RosterEntry } from "@/types";
import { SkeletonRosterEntry } from "@/components/ui/Skeleton";

export function RosterList() {
  const { data: games } = useGamesQuery();
  const { activeAccountId } = useAccountStore();
  const allEntries = useRosterStore((state) => state.entries);
  const initialized = useRosterStore((state) => state.initialized);
  const removeEntry = useRosterStore((state) => state.removeEntry);
  const restoreEntry = useRosterStore((state) => state.restoreEntry);
  const [removing, setRemoving] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<RosterEntry | null>(null);

  const { activeEntries, archivedEntries } = useMemo(() => {
    const filtered = activeAccountId 
      ? allEntries.filter((entry) => entry.accountId === activeAccountId)
      : allEntries;

    // Treat entries as archived if either the entry itself is archived OR the
    // underlying game has been marked `status: 'archived'` in Firestore. This
    // ensures campaigns that are finished do not occupy active slots in the UI.
    const activeEntries: typeof filtered = [];
    const archivedEntries: typeof filtered = [];

    filtered.forEach((entry) => {
      const game = games?.find((g) => g.id === entry.gameId);
      const gameIsArchived = game?.status === 'archived';

      if (entry.status === 'archived' || gameIsArchived) {
        archivedEntries.push(entry);
      } else {
        activeEntries.push(entry);
      }
    });

    return { activeEntries, archivedEntries };
  }, [allEntries, activeAccountId]);

  const gameMap = useMemo(() => {
    const map = new Map<string, { name: string }>();
    games?.forEach((game) => map.set(game.id, { name: game.name }));
    return map;
  }, [games]);

  const handleRemove = async (slotId: string) => {
    setRemoving(slotId);
    try {
      await removeEntry(slotId);
    } finally {
      setRemoving(null);
    }
  };

  const handleRestore = async (slotId: string) => {
    setRemoving(slotId);
    try {
      await restoreEntry(slotId);
    } finally {
      setRemoving(null);
    }
  };

  if (!initialized) {
    return (
      <div className="space-y-3">
        <SkeletonRosterEntry />
        <SkeletonRosterEntry />
        <SkeletonRosterEntry />
      </div>
    );
  }

  if (!activeEntries.length && !archivedEntries.length) {
    return (
      <div className="rounded-3xl border border-dashed border-white/20 bg-white/5 p-6 text-center text-sm text-white/70">
        <p className="mb-4 text-white/80">
          No games in this roster yet. Claim a campaign to unlock your affiliate link.
        </p>
        <Link to="/campaigns" className="inline-block w-full max-w-xs mx-auto">
          <button className="w-full rounded-2xl bg-lime-400 px-4 py-3 text-center font-semibold text-black hover:bg-lime-300">
            Browse campaigns
          </button>
        </Link>
      </div>
    );
  }

  const renderEntry = (entry: typeof activeEntries[0], isArchived = false) => {
    const game = games?.find((g) => g.id === entry.gameId);
    const gameName = gameMap.get(entry.gameId)?.name ?? entry.gameId;

    return (
      <RosterCard
        key={entry.slotId}
        entry={entry}
        gameName={gameName}
        gameSlug={game?.slug}
        gameStatus={game?.status}
        gameIcon={game?.appIcon}
        gameplayVideos={game?.gameplay}
        isArchived={isArchived}
        removing={removing === entry.slotId}
          onRemove={() => handleRemove(entry.slotId)}
          onRestore={() => handleRestore(entry.slotId)}
        onClick={() => setSelectedEntry(entry)}
      />
    );
  };

  const selectedGame = selectedEntry
    ? games?.find((g) => g.id === selectedEntry.gameId) ?? null
    : null;

  return (
    <>
      <div className="space-y-4">
        {/* Active Games */}
        <div className="space-y-3">
          {activeEntries.map((entry) => renderEntry(entry, false))}
        </div>

        {/* Archived Games */}
        {archivedEntries.length > 0 && (
          <div className="mt-6">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="mb-3 flex w-full items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white/80 transition hover:bg-white/10"
            >
              <span>Archived Games ({archivedEntries.length})</span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${showArchived ? "rotate-180" : ""}`}
              />
            </button>
            {showArchived && (
              <div className="space-y-3">
                {archivedEntries.map((entry) => renderEntry(entry, true))}
              </div>
            )}
          </div>
        )}
      </div>

      <GameEarningsSheet
        entry={selectedEntry}
        game={selectedGame}
        open={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
      />
    </>
  );
}
